import { LitElement, css, html } from 'lit';
import { customElement, property, state } from 'lit/decorators';
import { getFromLocalStorage, isDefined } from '../helpers/utils';
import { redeem_activated_challenge_reward, remove_activated_challenge_reward } from '../helpers/api/challenge-api';

import { ChallengeReward } from '../types';
import { StateController } from '../helpers/state/state-management/state-controller';
import accountInfo from '../helpers/state/account-info';
import challengeData from '../helpers/state/challenge-data';
import { createWalletLink } from '../helpers/urls';
import { detectMobileViewport } from '../helpers/device';
import featuresInfo from '../helpers/state/features-info';
import format from 'date-fns/format';
import { getMainPage } from '../helpers/page-name';
import pointCache from '../helpers/state/point-cache';
import topicData from '../helpers/state/topic-data';

@customElement('challenge-participant-progress')
export class ChallengeParticipantProgressComponent extends LitElement {
    static amplitude_callsite: string = 'Challenge Participant Progress Modal';

    @property({ type: Boolean })
    open: boolean = false;

    @state()
    title: string = '';

    @state()
    selectedTab: string = 'my_stats';

    @state()
    remainingRewards: ChallengeReward[] = [];

    @state()
    showCriteria: boolean = false;

    @state()
    activeChallengeReward?: ChallengeReward;

    @state()
    challengeIsHappening: boolean = false;

    private readonly bindPointCacheState = new StateController(this, pointCache);
    private readonly bindTopicState = new StateController(this, topicData);
    private readonly bindAccountInfo = new StateController(this, accountInfo);

    async firstUpdated(changedProperties: Map<string | number | symbol, unknown>) {

        await this.startup();
    }

    private async startup() {
        await challengeData.refreshChallenge();
        this.getRemainingRewards();
        this.title = challengeData.challenge?.name ?? '';
        this.selectedTab = 'my_stats';
    }

    get walletLink() {
        if (challengeData.challenge?.wallet_card_id && getFromLocalStorage(challengeData.challenge.wallet_card_id)) {
            return undefined;
        }
        return challengeData.challenge?.wallet_card_id ? createWalletLink(challengeData.challenge.wallet_card_id, accountInfo.email, accountInfo.fullName, topicData.topic?.custom_social_media_image ?? '', challengeData.activated_challenge?.point_count) : undefined;
    }

    private handleChangeTab(tab: string) {
        this.selectedTab = tab;
    }

    private openShowCriteria(e: CustomEvent){
        this.showCriteria = true;
        this.activeChallengeReward = e.detail.data;
    }

    // list remaining user awards by subtracting earned rewards from challenge rewards
    private getRemainingRewards() {
        const earnedRewards = challengeData.activated_challenge?.earned_rewards ?? [];
        this.remainingRewards = challengeData.challenge?.rewards.filter((reward) => !earnedRewards.some((earnedReward) => earnedReward._id?.$oid === reward._id?.$oid)) ?? [];
    }

    private openWallet() {
        window.open(this.walletLink, '_blank');
        // add data to local storage wallet-id = true
        if (challengeData.challenge?.wallet_card_id) {
            localStorage.setItem(challengeData.challenge.wallet_card_id, 'true');
        }
    }

    async removeRedemption(reward_id: string) {
        if (!isDefined(challengeData.activated_challenge)) return;
        if (!confirm('Are you sure you want to remove this redemption? This cannot be undone.')) {
            return;
        }

        challengeData.activated_challenge = await remove_activated_challenge_reward(
            challengeData.activated_challenge._id.$oid,
            topicData.topicKey,            reward_id,
        );

        this.dispatchEvent(
            new CustomEvent('on-save')
        );
        this.dispatchEvent(
            new CustomEvent('refresh-challenges')
        );
        getMainPage().errorToastElement.showToast('Redemption removed');
    }

    async redeemReward(reward_id: string) {
        if (!isDefined(challengeData.activated_challenge)) return;

        challengeData.activated_challenge = await redeem_activated_challenge_reward(
            challengeData.activated_challenge._id.$oid,
            topicData.topicKey,
            reward_id,
        );
        this.dispatchEvent(
            new CustomEvent('on-save')
        );
        this.dispatchEvent(
            new CustomEvent('refresh-challenges')
        );
        getMainPage().errorToastElement.showToast('Reward marked as redeemed');
    }


    render() {
        return html`
            <div class="modalContent">
                <div class="modal__content">
                    ${challengeData.challenge?.show_leaderboard !== false
                        ? html`
                            <div class="challenge-participant__tabs">
                                <div
                                    class="challenge-participant__tab left ${this.selectedTab === 'my_stats'
                                        ? 'active'
                                        : ''}"
                                    @click="${() => this.handleChangeTab('my_stats')}"
                                >
                                    <img src="/images/ic_user.svg" class="tab-icon" alt="User Icon" />
                                    My Stats
                                </div>
                                <div
                                    class="challenge-participant__tab right ${this.selectedTab ===
                                    'leaderboard'
                                        ? 'active'
                                        : ''}"
                                    @click="${() => this.handleChangeTab('leaderboard')}"
                                >
                                    <img src="/images/ic_map.svg" class="tab-icon" alt="Map Icon" />
                                    Leaderboard
                                </div>
                            </div>
                        `
                        : ''
                    }
                    ${this.selectedTab === 'my_stats' ? html`
                        <div class="challenge-participant__content">
                            <div class="challenge-participant__user-name">
                                <b>${challengeData.activated_challenge?.display_name ?? ''}</b> ${challengeData.activated_challenge?.email ?? ''}
                            </div>
                            ${this.challengeIsHappening && isDefined(this.walletLink) && detectMobileViewport()  ? html`
                                <div class="add-to-wallet__container">
                                    <div class="add-to-wallet__content" @click="${this.openWallet}">
                                        <img src="/images/add_to_wallet.png" class="add-to-wallet__icon" alt="Wallet Icon" style="width: 100%; height: auto;" />
                                    </div>
                                </div>
                            ` : ''}
                            <div class="stats-card__container">
                                <div class="stats-card">
                                    <div class="stats-card__value">${challengeData.activated_challenge?.point_count ?? 0}</div>
                                    <div class="stats-card__label">Points Earned</div>
                                </div>
                                <div class="stats-card">
                                    <div class="stats-card__value">${challengeData.activated_challenge?.checkin_count ?? 0}</div>
                                    <div class="stats-card__label">Places Visited</div>
                                </div>
                            </div>

                            ${challengeData.activated_challenge && challengeData.activated_challenge.earned_rewards.length > 0
                                ? html`
                                    <p class="title">Earned Awards</p>

                                    <div class="awards__container">
                                        ${challengeData.activated_challenge.earned_rewards.map((reward) => html`
                                            <div class="awards__card">
                                                <img
                                                    src="${reward.image_url}"
                                                    class="award-icon"
                                                    alt="${reward.title}"
                                                />
                                                <div class="awards__label">${reward.title}</div>
                                                ${reward.redeemable ? html`
                                                        ${((challengeData.activated_challenge?.redeemed_rewards?.some(redemption =>
                                                            redemption._id.$oid === reward?._id?.$oid
                                                        )) ?? false)
                                                        ? html`
                                                            <div class="award-count">Redeemed
                                                                <mwc-icon
                                                                    style="--mdc-icon-size: 12px; color: var(--main-moon-color); cursor: pointer;"
                                                                    @click="${async () => await this.removeRedemption(reward._id?.$oid ?? '')}"
                                                                    >delete</mwc-icon>
                                                            </div>
                                                        ` : html`
                                                            <div class="award-count">
                                                                <form-button
                                                                    variant="outline"
                                                                    size="xs"
                                                                    @click="${async () => await this.redeemReward(reward._id?.$oid ?? '')}"
                                                                    icon="redeem"
                                                                >Redeem</form-button>
                                                            </div>
                                                        `}
                                                ` : ''}
                                            </div>
                                        `)}
                                    </div>
                                `
                                : ''
                            }

                            ${this.remainingRewards.length > 0 ? html`
                            <p class="title">Remaining Awards</p>
                            <challenge-rewards-wrapper ?disabled="${!featuresInfo.challengePassportEnabled}"
                                .class=${'awards-wrapper'}
                                @dataAction=${this.openShowCriteria}
                                .data="${this.remainingRewards}"
                                .activeData=${this.activeChallengeReward}
                                .hideNames=${true}
                            ></challenge-rewards-wrapper>
                            ${this.showCriteria
                                ? html`
                                    <div class="challenge__rewards__criteria">
                                            <i>${this.activeChallengeReward?.title ?? ''}</i>: ${this.activeChallengeReward?.reward_criteria_threshold === 0 ? html`
                                            ${this.activeChallengeReward?.reward_criteria === 'number_of_places' ? html`Visit All Places` : html`Earn All Points`}` : html `
                                            ${this.activeChallengeReward?.reward_criteria === 'number_of_places'? html`Visit ${this.activeChallengeReward.reward_criteria_threshold} Places`: html`Earn ${this.activeChallengeReward?.reward_criteria_threshold}Points`}`}
                                            <br>
                                            ${this.activeChallengeReward?.description ?? ''}
                                    </div>
                                `
                                : ''
                            }` : '' }
                            ${challengeData.activated_checkin_details.length > 0
                                ? html`
                                    <p class="title">Check Ins</p>

                                    <div class="checkins__container">
                                        ${challengeData.activated_checkin_details.map((checkin) => html`
                                            <div class="checkins__row">
                                                <img
                                                    src="${checkin.categoryIcon}"
                                                    class="checkin-category-icon"
                                                    alt="${checkin.categoryName}"
                                                />
                                                <div class="checkins-content">
                                                    <div class="checkins-place">
                                                        ${checkin.placeName}
                                                    </div>
                                                    ${isDefined(checkin.all_timestamps) ? html`
                                                        <div class="checkins-time">
                                                            ${checkin.all_timestamps.map((timestamp) =>
                                                                    html`
                                                                    <span>${format(new Date(timestamp), 'MMM dd, yyyy @ h:mm a')}</span><br>
                                                                `)}                                                        </div>
                                                    ` : ''}
                                                </div>
                                                <div class="checkins-point">
                                                    ${checkin.pointValue * (checkin.counted_timestamps.length ?? 1)} Point${(checkin.counted_timestamps.length ?? 1) > 1 ? 's' : ''}
                                                    ${checkin.counted_timestamps.length > 1 ? html`
                                                        <br>
                                                        (${checkin.counted_timestamps.length} Check-Ins worth ${checkin.pointValue} Point${checkin.pointValue > 1 ? 's' : ''})
                                                    ` : ''}
                                                </div>
                                            </div>`
                                        )}
                                    </div>
                                `
                                : ''
                            }
                            ${isDefined(challengeData?.activated_challenge?.admin_assigned_points) && (challengeData?.activated_challenge?.admin_assigned_points.length ?? 0) > 0
                                ? html`
                                    <p class="title">Extra Points</p>
                                    <div class="checkins__container">
                                        ${challengeData?.activated_challenge?.admin_assigned_points.map((point) =>  html`
                                            <div class="checkins__row">
                                                <img
                                                    src="https://img.icons8.com/?size=48&amp;id=QbIcwOBghArh&amp;format=png"
                                                    class="checkin-category-icon"
                                                    alt="${point.description}"
                                                />
                                                <div class="checkins-content">
                                                    <div class="checkins-place">
                                                        ${point.description}
                                                    </div>
                                                    ${isDefined(point.created_at) ? html`
                                                        <div class="checkins-time">
                                                        ${format(new Date(point.created_at), 'MMM dd, yyyy @ h:mm a')}
                                                        </div>
                                                    ` : ''}
                                                </div>
                                                <div class="checkins-point">
                                                    ${point.value} Point
                                                </div>
                                            </div>`
                                        )}
                                    </div>
                                `
                                : ''
                            }
                        </div>
                    ` : html`
                        <div class="challenge-participant__content">
                            <p class="title">Challenge Leaders</p>

                            <div class="challenge-leaders__container">
                                ${challengeData.activated_challenges
                                    .sort((a, b) => (b.point_count ?? 0) - (a.point_count ?? 0))
                                    .map((activatedChallenge) => html`
                                    <div class="challenge-leaders__row ${challengeData.activated_challenge?._id.$oid === activatedChallenge._id.$oid ? 'active' : ''}">
                                        <div class="challenge-leaders__content">
                                            <div class="challenge-leaders__user-name">
                                                ${activatedChallenge.display_name ?? ''}
                                                ${activatedChallenge.earned_rewards.map((reward) => html`
                                                    <img src="${reward.image_url}" class="award-icon" alt="${reward.title}"/>
                                                `)}
                                            </div>
                                            <div class="challenge-leaders__places">
                                                ${activatedChallenge.checkin_count ?? 0} Places Visited
                                            </div>
                                        </div>
                                        <div class="challenge-leaders__point">
                                            ${activatedChallenge.point_count ?? 0} Points
                                        </div>
                                    </div>
                                `)}
                            </div>
                        </div>
                    `}
                </div>
            </div>
            <error-toast id="errorToast"></error-toast>
        `;
    }

    static get styles() {
        return css`
            :host {
                --modal-width: 550px;
            }

            .modalContent {
                position: relative;
                overflow-y: auto;
                box-sizing: border-box;
                height: 100%;
                display: flex;
                flex-direction: column;
                padding: 0 1rem;
            }

            .modal__header {
                width: 100%;
                text-align: center;
                padding: 5px 10px;
                border-bottom: solid 1px #efecf3;
                color: var(--theme-input-color);
                font-size: 1prem;
                font-weight: 700;
                line-height: 1rem;
                box-sizing: border-box;
            }

            .modal__content {
                padding: 10px 0;
                width: 100%;
                box-sizing: border-box;
                flex: 1;
                height: 100%;
                display: flex;
                flex-direction: column;
            }

            .challenge-participant__tabs {
                width: 100%;
                display: flex;
                align-items: center;
                justify-content: center;
                border-radius: 8px;
                margin-bottom: 10px;
            }

            .challenge-participant__tab {
                flex: 1;
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: 16px;
                line-height: 150%;
                color: var(--main-primary-color);
                background: rgba(153, 153, 153, 0.48);
                padding: 5px 10px;
                cursor: pointer;
            }

            .challenge-participant__tab.right {
                border-radius: 0px 8px 8px 0px;
            }

            .challenge-participant__tab.left {
                border-radius: 8px 0px 0px 8px;
            }

            .challenge-participant__tab.active {
                background: var(--main-invalid-color);
            }

            .tab-icon {
                width: 24px;
                height: 24px;
                margin-right: 5px;
            }

            .challenge-participant__content {
                width: 100%;
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: flex-start;
                gap: 12px;
                overflow-y: auto;
                flex:1;
            }

            .challenge-participant__user-name {
                color: #000000;
                text-align: center;
                line-height: 125%;
            }

            .challenge-participant__user-name > b {
                font-weight: 700;
            }

            .stats-card__container {
                width: 100%;
                display: flex;
                justify-content: center;
                align-items: flex-start;
                gap: 6px;
            }

            .stats-card {
                flex: 1;
                max-width: 180px;
                display: flex;
                align-items: center;
                justify-content: center;
                height: 40px;
                padding: 0 16px;
                border-radius: 12px;
                border: 1px solid #CAC4D0;
                gap: 4px;
                box-sizing: border-box;
            }

            .stats-card__value {
                font-size: 24px;
                font-weight: 700;
                line-height: 130%;
                color: #000000;
            }

            .stats-card__label {
                color: #1D1B20;
                text-align: center;
                line-height: 125%;
            }

            p.title {
                color: #000000;
                text-align: center;
                font-size: 16px;
                font-weight: 700;
                line-height: 150%;
                margin: 0;
            }

            .awards__container {
                max-width: 100%;
                display: flex;
                align-items: flex-start;
                justify-content: flex-start;
                overflow-x: auto;
                gap: 9px;
                padding-bottom: 10px;
                box-sizing: border-box;
            }

            .awards__card {
                display: flex;
                padding: 0px 5px;
                flex-direction: column;
                align-items: center;
                gap: 5px;
                flex: 1;
            }

            .award-icon {
                width: 62px;
                height: 62px;
            }

            .awards__label {
                color: #000000;
                text-align: center;
                line-height: 125%;
            }

            .checkins__container {
                width: 100%;
                flex-direction: column;
                align-items: flex-start;
                gap: 10px;
                flex: 1;
                overflow-y: auto;
            }

            .checkins__row {
                display: flex;
                padding: 8px 24px 8px 16px;
                justify-content: center;
                align-items: center;
                gap: 16px;
                box-sizing: border-box;
                border-bottom: solid 1px #CAC4D0;
            }

            .checkin-category-icon {
                width: 24px;
                height: 24px;
            }

            .checkins-content {
                display: flex;
                flex-direction: column;
                align-items: flex-start;
                flex: 1 0 0;
            }

            .checkins-place {
                color: #1D1B20;
                font-size: 16px;
                font-family: Roboto;
                line-height: 24px;
                letter-spacing: 0.5px;
            }

            .checkins-time {
                color: #49454F;
                font-size: 12px;
                line-height: 120%;
            }

            .checkins-point{
                color: #49454F;
                text-align: right;
                font-size: 11px;
                font-family: Roboto;
                font-weight: 500;
                line-height: 16px;
                letter-spacing: 0.5px;
            }

            .challenge-leaders__container {
                width: 100%;
                display: flex;
                flex-direction: column;
                align-items: flex-start;
                gap: 10px;
            }

            .challenge-leaders__row {
                width: 100%;
                display: flex;
                justify-content: center;
                align-items: center;
                gap: 16px;
                border-bottom: solid 1px #CAC4D0;
                box-sizing: border-box;
                padding: 0 16px;
                min-height: 64px;
            }

            .challenge-leaders__row.active {
                border: 1px solid #EA8567;
            }

            .challenge-leaders__content {
                display: flex;
                flex-direction: column;
                align-items: flex-start;
                flex: 1 0 0;
            }

            .challenge-leaders__user-name {
                color: #1D1B20;
                font-size: 16px;
                font-family: Roboto;
                line-height: 24px;
                letter-spacing: 0.5px;
                display: inline;
            }

            .award-icon {
                width: 32px;
                height: 32px;
                margin-left: 5px;
            }

            .challenge-leaders__places {
                color: #49454F;
                font-size: 12px;
                line-height: 120%;
            }

            .challenge-leaders__point {
                color: #49454F;
                text-align: right;
                font-size: 11px;
                font-family: Roboto;
                font-weight: 500;
                line-height: 16px;
                letter-spacing: 0.5px;
            }

            @media only screen and (max-width: 550px) {
                .modalContent {
                    width: 100%;
                    min-height: 100%;
                    max-height: 100%;
                }
            }
        `;
    }
}
